import React from 'react';

const cx = (...a) => a.filter(Boolean).join(' ');

const gapMap = {
    xs: 'gap-1',
    sm: 'gap-2',
    md: 'gap-4',
    lg: 'gap-6',
    xl: 'gap-8',
};

const axisGap = (axis, v) => {
    if (typeof v === 'number') return `${axis === 'row' ? 'gap-y' : 'gap-x'}-${v}`;
    const map = {
        xs: axis === 'row' ? 'gap-y-1' : 'gap-x-1',
        sm: axis === 'row' ? 'gap-y-2' : 'gap-x-2',
        md: axis === 'row' ? 'gap-y-4' : 'gap-x-4',
        lg: axis === 'row' ? 'gap-y-6' : 'gap-x-6',
        xl: axis === 'row' ? 'gap-y-8' : 'gap-x-8',
    };
    return map[v] || '';
};

const alignContentMap = {
    start: 'content-start',
    center: 'content-center',
    end: 'content-end',
    between: 'content-between',
    around: 'content-around',
    evenly: 'content-evenly',
    stretch: 'content-stretch',
};

const alignItemsMap = {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
};

const justifyItemsMap = {
    start: 'justify-items-start',
    center: 'justify-items-center',
    end: 'justify-items-end',
    stretch: 'justify-items-stretch',
};

const colsToClass = (n, prefix = '') => {
    if (!n) return '';
    const clamp = Math.max(1, Math.min(12, Number(n)));
    return `${prefix}grid-cols-${clamp}`;
};

function GridBase({
    as: As = 'div',
    children,
    cols = 1,
    sm,
    md,
    lg,
    xl,
    gap = 'md',
    rowGap,
    colGap,
    dense = false,
    alignContent = 'start',
    alignItems = 'stretch',
    justifyItems = 'stretch',
    template,
    autoFit,
    minColWidth,
    fullWidth = true,
    className = '',
    style,
}) {
    const base = 'grid';
    const gapClass = typeof gap === 'number' ? `gap-${gap}` : gapMap[gap] || '';
    const rowGapClass = rowGap ? axisGap('row', rowGap) : '';
    const colGapClass = colGap ? axisGap('col', colGap) : '';

    const colsClass = colsToClass(cols);
    const smClass = colsToClass(sm, 'sm:');
    const mdClass = colsToClass(md, 'md:');
    const lgClass = colsToClass(lg, 'lg:');
    const xlClass = colsToClass(xl, 'xl:');

    const templateStyle =
        template
            ? { gridTemplateColumns: template }
            : autoFit && minColWidth
                ? { gridTemplateColumns: `repeat(auto-fit, minmax(${minColWidth}, 1fr))` }
                : {};

    const classes = cx(
        base,
        fullWidth && 'w-full',
        gapClass,
        rowGapClass,
        colGapClass,
        colsClass,
        smClass,
        mdClass,
        lgClass,
        xlClass,
        dense && 'grid-flow-dense',
        alignContentMap[alignContent] || '',
        alignItemsMap[alignItems] || '',
        justifyItemsMap[justifyItems] || '',
        className
    );

    return (
        <As className={classes} style={{ ...style, ...templateStyle }}>
            {children}
        </As>
    );
}

const spanToClass = (n, axis = 'col', prefix = '') => {
    if (!n) return '';
    const clamp = Math.max(1, Math.min(12, Number(n)));
    return `${prefix}${axis}-span-${clamp}`;
};

function GridItem({
    as: As = 'div',
    children,
    colSpan,
    rowSpan,
    sm,
    md,
    lg,
    xl,
    rowSm,
    rowMd,
    rowLg,
    rowXl,
    order,
    alignSelf,
    justifySelf,
    className = '',
    style,
}) {
    const classes = cx(
        colSpan && spanToClass(colSpan, 'col'),
        rowSpan && spanToClass(rowSpan, 'row'),
        sm && spanToClass(sm, 'col', 'sm:'),
        md && spanToClass(md, 'col', 'md:'),
        lg && spanToClass(lg, 'col', 'lg:'),
        xl && spanToClass(xl, 'col', 'xl:'),
        rowSm && spanToClass(rowSm, 'row', 'sm:'),
        rowMd && spanToClass(rowMd, 'row', 'md:'),
        rowLg && spanToClass(rowLg, 'row', 'lg:'),
        rowXl && spanToClass(rowXl, 'row', 'xl:'),
        alignSelf && `self-${alignSelf}`,
        justifySelf && `justify-self-${justifySelf}`,
        typeof order === 'number' ? `order-${order}` : '',
        className
    );

    return (
        <As className={classes} style={style}>
            {children}
        </As>
    );
}

function Grid(props) {
    return <GridBase {...props} />;
}
Grid.Item = GridItem;

export default Grid;